<template>
  <div>
    <b-form @submit="saveProfile">
      <b-form-group label="Phone number">
        <E164PhoneNumberInput v-model="profile.phone_number" :required="true" />
        <b-form-text v-if="showNeedsCodeVerification">
          Your phone number hasn't been verified. <b-link href="#" v-on:click="showCodeVerificationModal = true">Request verification</b-link>
        </b-form-text>
        <b-form-invalid-feedback :state="isValidPhoneNumber">
          Please enter a valid phone number.
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Your time zone" label-for="profile-time-zone">
        <b-form-select id="profile-time-zone" v-model="profile.time_zone" :options="timeZoneOptions" />
        <b-form-text>
          You will get reminder texts around 9 in the morning your time.
        </b-form-text>
      </b-form-group>
      <b-form-group v-if="profile.has_verified_once">
        <b-form-checkbox id="profile-texts-disabled" v-model="profile.texts_disabled">
          Disable texts
        </b-form-checkbox>
      </b-form-group>
      <b-button type="submit" variant="primary" :disabled="isSavingProfile">Save</b-button>
      <b-alert v-model="showSavedAlert" variant="light">Settings saved</b-alert>
    </b-form>

    <b-modal v-model="showCodeVerificationModal" title="Verify your number" hide-footer @close="closeCodeVerificationModal">
      <p>We just sent a text with a 6 digit code to your number {{ profile.phone_number }} (this text comes from a US number with country code +1). Enter the code to verify your number.</p>
      <b-form @submit="verifyCode">
        <b-form-group>
          <b-form-input v-model="enteredVerificationCode" placeholder="000000" autofocus required @input="clearVerificationCodeValidation" />
          <b-form-invalid-feedback :state="isValidVerificationCode">
            Please enter a 6 digit code.
          </b-form-invalid-feedback>
          <b-form-invalid-feedback :state="isMatchingVerificationCode">
            The code you gave did not match the code we sent you. Try again.
          </b-form-invalid-feedback>
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="isSavingProfile">Verify</b-button>
      </b-form>
      <b-form id="form-new-code" @submit="requestCode">
        <p>If you haven't received the code by text, try requesting a new one here.</p>
        <b-button type="submit" variant="primary" :disabled="isRequestingCode">Request new code</b-button>
        <b-form-text v-if="codeRequested">
          We just sent a new code to {{ profile.phone_number }}. Verify it above.
        </b-form-text>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { BAlert, BButton, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BFormSelect, BFormText, BLink, BModal } from 'bootstrap-vue'
import * as moment from 'moment-timezone'; // This is probably very heavy-weight ...
import E164PhoneNumberInput from '../common/e164_phone_number_input';
import GenericErrorMixin from '../../libs/generic_error_mixin'
import { jsonApi } from '../../libs/jsonapi'

export default {
  mixins: [GenericErrorMixin],
  mounted: async function () {
    this.profileData = this.profile;
    if (!this.profileData.time_zone) {
      this.profileData.time_zone = moment.tz.guess();
    }
  },
  props: {
    profile: Object
  },
  data: function () {
    return {
      codeRequested: false,
      enteredVerificationCode: null,
      isMatchingVerificationCode: null,
      isRequestingCode: false,
      isSavingProfile: false,
      isValidPhoneNumber: null,
      isValidVerificationCode: null,
      jsonApi: jsonApi,
      profileData: {},
      showCodeVerificationModal: false,
      showSavedAlert: false
    }
  },
  computed: {
    showNeedsCodeVerification() {
      return this.profileData.has_verified_once && !this.profileData.is_verification_code_matching;
    },
    timeZoneOptions() {
      // LOL
      const zones = moment.tz.names().map((name) => moment.tz.zone(name));
      const now = moment().unix();
      zones.sort((a, b) => {
        const aOffset = -a.utcOffset(now);
        const bOffset = -b.utcOffset(now);
        const compared = aOffset - bOffset;
        if (compared == 0) {
          return a.name.localeCompare(b.name);
        }
        return compared;
      });
      return zones.map((zone) => {
        const betterName = zone.name.replace(/_/g, ' ');
        const utcOffset = -zone.utcOffset(now);
        const text = `${betterName} (GMT ${utcOffset >= 0 ? '+' : ''}${utcOffset / 60})`;
        return { value: zone.name, text: text };
      });
    }
  },
  methods: {
    closeCodeVerificationModal() {
      this.showCodeVerificationModal = false;
      this.codeRequested = false;
      this.isMatchingVerificationCode = null;
      this.isValidVerificationCode = null;
    },
    async saveProfile(evt) {
      evt.preventDefault();
      this.isSavingProfile = true;
      this.isValidPhoneNumber = true;

      try {
        const response = await this.jsonApi.update('profile',
          {id: this.profileData.id, phone_number: this.profileData.phone_number, time_zone: this.profileData.time_zone, texts_disabled: this.profileData.texts_disabled});
        this.profileData = response.data;
        this.$emit('profile-updated', this.profileData);
        this.showSavedAlert = 5;
      } catch (error) {
        if (error.phone_number) {
          this.isValidPhoneNumber = false;
        } else {
          this.showGenericErrorModal();
        }
        return;
      } finally {
        this.isSavingProfile = false;
      }

      if (!this.profileData.is_verification_code_matching) {
        this.showCodeVerificationModal = true;
      }
    },
    async verifyCode(evt) {
      evt.preventDefault();

      if (!this._validateCode(this.enteredVerificationCode)) {
        this.isValidVerificationCode = false;
        return;
      }

      const isFirstVerification = !this.profileData.has_verified_once;
      this.isSavingProfile = true;

      try {
        const response = await this.jsonApi.update('profile', {id: this.profileData.id, entered_verification_code: this.enteredVerificationCode});
        this.profileData = response.data;
        this.$emit('profile-updated', this.profileData);
      } catch (error) {
        this.showGenericErrorModal();
        return;
      } finally {
        this.isSavingProfile = false;
        this.enteredVerificationCode = null;
      }

      if (this.profileData.is_verification_code_matching) {
        this.closeCodeVerificationModal();
        this.$emit('first-verification-done');
      } else {
        this.isMatchingVerificationCode = false;
      }
    },
    async requestCode(evt) {
      evt.preventDefault();
      this.codeRequested = false;
      this.isRequestingCode = true;

      try {
        const response = await this.jsonApi.update('profile', {id: this.profileData.id, request_code: true});
        this.profileData = response.data;
        this.$emit('profile-updated', this.profileData);
        this.codeRequested = true;
      } catch (error) {
        this.showGenericErrorModal();
        return;
      } finally {
        this.isRequestingCode = false;
      }
    },
    clearVerificationCodeValidation() {
      this.isValidVerificationCode = null;
      this.isMatchingVerificationCode = null;
    },
    _validateCode(code) {
      return !!code.match(/^\d{6}$/);
    }
  },
  components: {
    'b-alert': BAlert,
    'b-button': BButton,
    'b-form': BForm,
    'b-form-group': BFormGroup,
    'b-form-input': BFormInput,
    'b-form-invalid-feedback': BFormInvalidFeedback,
    'b-form-select': BFormSelect,
    'b-form-text': BFormText,
    'b-link': BLink,
    'b-modal': BModal,
    E164PhoneNumberInput,
  }
}
</script>

<style scoped>
#form-new-code {
  margin-top: 2em;
}
</style>